'use client';

import { useState } from 'react';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  Text,
  Button,
  Badge,
  EnhancedCollapsibleWrapper,
  MarkdownPreview,
} from '@terra/ui-design-system';
import {
  SourceCodeIcon,
  FileCodeIcon,
  File01Icon,
  GitBranchIcon,
  Globe02Icon,
  Key01Icon,
  Mail01Icon,
  HierarchyIcon,
  PuzzleIcon,
  WebhookIcon,
} from '@terra/ui-icons';
import type { ApplicationEntryPoint } from '@terra/shared-schemas-git';

type SubEntryPoint = {
  method: string;
  routePath?: string | null;
  params?: string | null;
  description: string;
  codeSnippet: string;
  enhancement?: {
    fullPath: string;
    description: string;
    graphId: string;
    params?: string | null;
  } | null;
  endpointId?: string | null;
  reachable?: boolean | null;
};
interface EntryPointCardProps {
  entryPoint: {
    fileName?: string | null;
    description?: string | null;
    codeSnippet?: string | null;
    entryPoint?: ApplicationEntryPoint['entryPoint'] | null;
    id: string;
    investigated?: boolean | null;
    isReachable?: boolean | null;
    subEntryPoints?: SubEntryPoint[];
  };
  onViewGraph: (entryPoint: EntryPointCardProps['entryPoint']) => void;
}
const ENTRY_POINT_ICONS: Record<
  ApplicationEntryPoint['entryPoint'],
  React.ReactNode
> = {
  API_ENDPOINT: <Globe02Icon size={16} />,
  AUTH_MECHANISM: <Key01Icon size={16} />,
  WEBHOOK_HANDLER: <WebhookIcon size={16} />,
  WEBSOCKET_ENDPOINT: <HierarchyIcon size={16} />,
  GRAPHQL_RESOLVER: <PuzzleIcon size={16} />,
  SOAP_ENDPOINT: <Mail01Icon size={16} />,
  EXTERNAL_INTEGRATION: <HierarchyIcon size={16} />,
  MCP_TOOL: <PuzzleIcon size={16} />,
  OTHER: <FileCodeIcon size={16} />,
};
const ENTRY_POINT_LABELS: Record<ApplicationEntryPoint['entryPoint'], string> =
  {
    API_ENDPOINT: 'API Endpoint',
    AUTH_MECHANISM: 'Auth Mechanism',
    WEBHOOK_HANDLER: 'Webhook Handler',
    WEBSOCKET_ENDPOINT: 'WebSocket',
    GRAPHQL_RESOLVER: 'GraphQL Resolver',
    SOAP_ENDPOINT: 'SOAP Endpoint',
    EXTERNAL_INTEGRATION: 'External Integration',
    MCP_TOOL: 'MCP Tool',
    OTHER: 'Other',
  };
const getLanguageFromFileName = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  switch (extension) {
    case 'ts':
    case 'tsx':
      return 'typescript';
    case 'js':
    case 'jsx':
      return 'javascript';
    case 'py':
      return 'python';
    case 'java':
      return 'java';
    case 'go':
      return 'go';
    case 'rs':
      return 'rust';
    default:
      return 'text';
  }
};

export function EntryPointCard({
  entryPoint,
  onViewGraph,
}: EntryPointCardProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const hasDescription = !!entryPoint.description;
  const hasCode = !!entryPoint.codeSnippet;
  const hasSubEntryPoints =
    entryPoint.subEntryPoints && entryPoint.subEntryPoints.length > 0;
  const entryPointType = entryPoint.entryPoint || 'OTHER';
  const icon = ENTRY_POINT_ICONS[entryPointType];
  const label = ENTRY_POINT_LABELS[entryPointType];

  return (
    <Box px={3} py={2} bg="bg.default" _hover={{ bg: 'bg.subtle' }} w="full">
      <VStack gap={2} alignItems="stretch" w="full">
        {/* Header - More compact */}
        <HStack
          justifyContent="space-between"
          alignItems="center"
          w="full"
          gap={2}
        >
          <HStack gap={2} alignItems="center" flex="1" minW="0">
            <Box color="fg.muted" flexShrink={0}>
              {icon}
            </Box>
            <VStack gap={0} alignItems="flex-start" flex="1" minW="0">
              <Text
                fontSize="xs"
                fontWeight="medium"
                color="fg.default"
                truncate
              >
                {label}
              </Text>
              {entryPoint.fileName && (
                <Text fontSize="2xs" color="fg.muted" truncate w="full">
                  {entryPoint.fileName}
                </Text>
              )}
            </VStack>

            {/* Compact badges */}
            <HStack gap={1} flexShrink={0}>
              {entryPoint.investigated && (
                <Badge size="sm" variant="emphasis">
                  Analyzed
                </Badge>
              )}
              {entryPoint.isReachable && (
                <Badge size="sm" variant="emphasis">
                  Reachable
                </Badge>
              )}
              {hasSubEntryPoints && (
                <Badge size="sm" variant="outline">
                  {entryPoint.subEntryPoints?.length}
                </Badge>
              )}
            </HStack>
          </HStack>

          {/* View Graph Button */}
          <Button
            size="xs"
            variant="ghost"
            onClick={() => onViewGraph(entryPoint)}
            disabled={!entryPoint.investigated}
            flexShrink={0}
          >
            <GitBranchIcon size={12} />
          </Button>
        </HStack>

        {/* Description Section */}
        {hasDescription && (
          <EnhancedCollapsibleWrapper
            open={showDetails}
            triggerIcon={<File01Icon size={14} />}
            triggerContent={<Text fontSize="xs">Description</Text>}
            onOpenChange={setShowDetails}
            triggerSize="xs"
            colorScheme="gray"
          >
            <Text fontSize="sm" color="fg.muted" lineHeight="relaxed">
              {entryPoint.description}
            </Text>
          </EnhancedCollapsibleWrapper>
        )}

        {/* Code Snippet Section */}
        {hasCode && (
          <EnhancedCollapsibleWrapper
            open={showCode}
            triggerIcon={<SourceCodeIcon size={14} />}
            triggerContent={<Text fontSize="xs">Code Snippet</Text>}
            onOpenChange={setShowCode}
            triggerSize="xs"
            colorScheme="gray"
          >
            <MarkdownPreview
              source={`\`\`\`${getLanguageFromFileName(entryPoint.fileName || '')} showLineNumbers\n${entryPoint.codeSnippet}\n\`\`\``}
            />
          </EnhancedCollapsibleWrapper>
        )}
      </VStack>
    </Box>
  );
}
